// WhitelabelSignin — the standalone, record-driven branded sign-in surface
// (WHITELABEL-BRANDED-BOOT-1).
//
// Nika, 2026-08-19: "make it beautiful and branded just for bellforge and
// dont chain it to workspace code - its bellforge specific", and later
// "https://app.bellforge.eu/login does not render bellforge login screen".
//
// ── Why it is rendered from the signin PAGE, not registered as a route ───
// '/signin' and '/login' are registered in THREE page maps
// (shared/pages/identity-index.js, workspace/pages/index.js,
// canvas/pages/index.js) which are merged at boot in an order that silently
// overwrote a route-level registration: measured 2026-08-19,
// shellPages['/signin'] held the branded page while the live
// context.pages['/signin'] still resolved the shared one, and gating all
// three did not win. So this is a COMPONENT the shared signin page renders
// instead of its LoginWindow when a brand is present — the page that already
// renders cannot be out-voted.
//
// ── It chains to nothing ────────────────────────────────────────────────
// It extends no component and wraps no component; it only calls the same SDK
// auth methods the shared login does. That matters: a project component
// named `LoginWindow` doing `extends: 'LoginWindow'` COLLAPSES the shared one
// (it itself extends ModalWindow) — that shipped once, dropped the entire
// Google/GitHub/email column, and had to be reverted (workspace cbbf88fa0).
//
// Everything visible comes from the org's `branding` record resolved pre-auth
// (workspace functions/whitelabelBrand.js). Nothing here knows what
// "bellforge" is — the next white-label org gets the same surface from its
// own record, with no deploy.

// ⚠️ KEY-NAME DISCIPLINE: a bare child key AUTO-EXTENDS a built-in or
// registered component of the same name. Two shipped bugs came from exactly
// this and both looked like styling faults: a child keyed `Mark` rendered an
// HTML <mark> (UA default background — measured rgb(255,255,0) behind the
// logo, "remove yellow bg on bellforge logo"), and a child keyed `Logo`
// resolved to the brand `Logo` component and rendered as an <a> with its own
// content, so the injected SVG never appeared. Every child key here is
// deliberately non-colliding (BrandGlyph, CardHead, AccentBar, HeadText,
// AccentRule, OrRow, PersonaTile, PersonaName, GoogleBtn, GoogleLabel, SubmitBtn) — do not
// "tidy" them back to generic names.
//
// ⚠️ CSS DISCIPLINE FOR THIS FILE, learned the hard way on the first draft:
//  - NO `border:` shorthand. Use `boxShadow: 'inset 0 0 0 1px …'` for a
//    hairline — the shorthand-with-raw-value path is the documented
//    outline/border shorthand token trap and it took the CSS engine down.
//  - NO shared object spread into two child slots, and no reading a custom
//    prop off `el.parent`. Each card declares its own title inline.
// ── Palette ──────────────────────────────────────────────────────────────
// Colours are literal, not tokens, because this surface paints its OWN
// ground and therefore cannot inherit the shell's adaptive inks — a
// `title`/`caption` token here resolves against the SHELL theme and can land
// dark-on-dark. So the surface carries BOTH schemes itself (Nika 2026-08-19:
// "make both versions - light by default") and picks one per render.
//
// Only the ACCENT comes from the record in both schemes; the grounds are
// per-scheme: a record's `background` is its DARK ground (bellforge navy),
// and `backgroundLight` its light one, each falling back to a neutral so an
// org that supplies neither still gets a correct, readable surface.
const GOLD = '#D9A93B'

// `cardShadow` = the hairline PLUS a soft lift, so the card reads as one
// object on the tinted ground (Nika 2026-10-14, "make it more beautiful").
// `rule` is the divider ink; `glow` is how much of the tenant accent tints
// the ground behind the card (whitelabelBackdrop) — stronger on dark, where
// the same mix reads fainter.
const SCHEMES = {
  light: {
    ground: '#F4F4F2',
    card: '#FFFFFF',
    hairline: 'inset 0 0 0 1px rgba(20, 22, 30, 0.10)',
    cardShadow:
      'inset 0 0 0 1px rgba(20, 22, 30, 0.08), 0 1px 2px rgba(20, 22, 30, 0.04), 0 24px 48px -24px rgba(20, 22, 30, 0.18)',
    rule: 'rgba(20, 22, 30, 0.10)',
    glow: '16%',
    ink: 'rgba(17, 20, 28, 0.94)',
    inkSoft: 'rgba(17, 20, 28, 0.60)',
    field: 'rgba(255, 255, 255, 0.92)',
    rowHover: 'rgba(20, 22, 30, 0.05)',
    onAccent: '#1F2A44',
    danger: '#A83520'
  },
  dark: {
    ground: '#0C0C0D',
    card: 'rgba(255, 255, 255, 0.045)',
    hairline: 'inset 0 0 0 1px rgba(255, 255, 255, 0.09)',
    cardShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.09), 0 24px 48px -24px rgba(0, 0, 0, 0.6)',
    rule: 'rgba(255, 255, 255, 0.10)',
    glow: '24%',
    ink: 'rgba(255, 255, 255, 0.94)',
    inkSoft: 'rgba(255, 255, 255, 0.62)',
    field: 'rgba(255, 255, 255, 0.06)',
    rowHover: 'rgba(255, 255, 255, 0.06)',
    onAccent: '#1F2A44',
    danger: '#FF8C78'
  }
}

// The interaction washes every control on this surface shares (Rule 65 /
// Rule 67): a tint of the control's OWN ink, so one value is right on both
// schemes and on any tenant ground. Bound once, referenced everywhere.
const WASH_HOVER =
  'linear-gradient(color-mix(in srgb, currentColor 7%, transparent), color-mix(in srgb, currentColor 7%, transparent))'
const WASH_ACTIVE =
  'linear-gradient(color-mix(in srgb, currentColor 12%, transparent), color-mix(in srgb, currentColor 12%, transparent))'
// The focus ring is an OUTLINE, never a box-shadow: reactive props are written
// inline in this build, so a control's own reactive `boxShadow` hairline would
// beat any `:focus` box-shadow and the ring would never show.
const FOCUS_RING = 'color-mix(in srgb, currentColor 45%, transparent)'

// ── Control shapes ───────────────────────────────────────────────────────
// FACTORIES, not shared objects: each call returns a FRESH object, so no two
// slots ever hold the same reference (the CSS-discipline note above). One
// geometry for every control on the card — the control height, the control
// radius and one horizontal inset — so the field, the primary and the
// secondary button line up edge to edge (Nika's standing rule: symmetry).
const wlCaption = (key) => ({
  tag: 'label',
  fontSize: 'Z',
  fontWeight: '500',
  margin: 'Z 0 0',
  color: (el, s) => schemeOf(s).ink,
  text: '{{ ' + key + ' | polyglot }}'
})

const wlField = (extra) => ({
  tag: 'input',
  width: '100%',
  minHeight: 'controlH',
  padding: '0 Z1',
  round: 'radiusControl',
  fontSize: 'Z1',
  borderWidth: '0',
  background: (el, s) => schemeOf(s).field,
  color: (el, s) => schemeOf(s).ink,
  boxShadow: (el, s) => schemeOf(s).hairline,
  transition: 'B defaultBezier',
  transitionProperty: 'box-shadow, background-image',
  ':hover': { backgroundImage: WASH_HOVER },
  ':focus': {
    outlineStyle: 'solid',
    outlineWidth: 'hairline2',
    outlineOffset: 'hairline',
    outlineColor: FOCUS_RING
  },
  ...extra
})

// The primary action: the tenant accent as the FILL, its ink derived for AA
// (onAccentFor). Hover/active step the fill itself (brightness), never the
// label's opacity (Rule 65).
const wlPrimary = (extra) => ({
  tag: 'button',
  width: '100%',
  minHeight: 'controlH',
  padding: '0 A',
  round: 'radiusControl',
  borderWidth: '0',
  cursor: 'pointer',
  fontSize: 'Z1',
  fontWeight: '700',
  color: (el, s) => onAccentFor(s),
  background: (el, s) => accentOf(s),
  transition: 'B defaultBezier',
  transitionProperty: 'filter',
  ':hover': { filter: 'brightness(1.08)' },
  ':active': { filter: 'brightness(0.92)' },
  ':focus-visible': {
    outlineStyle: 'solid',
    outlineWidth: 'hairline2',
    outlineOffset: 'hairline2',
    outlineColor: 'CanvasText'
  },
  ...extra
})

// The secondary action: an outlined control in the scheme's ink.
const wlSecondary = (extra) => ({
  tag: 'button',
  type: 'button',
  width: '100%',
  minHeight: 'controlH',
  padding: '0 A',
  display: 'flex',
  align: 'center center',
  gap: 'Z',
  round: 'radiusControl',
  borderWidth: '0',
  cursor: 'pointer',
  fontSize: 'Z1',
  fontWeight: '500',
  color: (el, s) => schemeOf(s).ink,
  background: 'transparent',
  boxShadow: (el, s) => schemeOf(s).hairline,
  transition: 'B defaultBezier',
  transitionProperty: 'background-image, box-shadow',
  ':hover': { backgroundImage: WASH_HOVER },
  ':active': { backgroundImage: WASH_ACTIVE },
  ':focus-visible': {
    outlineStyle: 'solid',
    outlineWidth: 'hairline2',
    outlineOffset: 'hairline',
    outlineColor: FOCUS_RING
  },
  ...extra
})

const brandOf = (s) => (s && s.root && s.root.whitelabelBrand) || null

// Which scheme this surface paints in. LIGHT BY DEFAULT, deliberately —
// Nika's precedence for white-label is that the product's own choice
// overrides the OS, so the OS preference is NOT consulted here:
//   1. the visitor's OWN explicit choice, if they have made one
//   2. the org's `branding.theme`
//   3. light
// ⚠️ Step 1 must distinguish "explicitly chose auto" from "never chose" —
// both normalize to the string 'auto'. A raw-key read is the only way to
// tell, the same distinction `hasNoStoredThemeMode` exists for in
// themeMode.js. An explicit 'auto' means "follow my OS", so it defers to the
// OS rather than to the brand.
// Exported (2026-08-30, WhitelabelRequestAccess): the ws-side authed
// request-access gate paints with the SAME scheme precedence as this card.
// Importing beats re-deriving — two copies of the precedence rule drift
// (the exact argument MainLoader.js makes for whitelabelGround).
export const schemeOf = (s) => {
  const b = brandOf(s)
  let stored = null
  try {
    if (typeof localStorage !== 'undefined') stored = localStorage.getItem('themeMode')
  } catch (_) {}
  if (stored === 'light' || stored === 'dark') return SCHEMES[stored]
  if (stored === 'auto') {
    let prefersDark = false
    try {
      prefersDark = !!(
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
      )
    } catch (_) {}
    return prefersDark ? SCHEMES.dark : SCHEMES.light
  }
  const recorded = b && b.theme
  if (recorded === 'dark' || recorded === 'light') return SCHEMES[recorded]
  return SCHEMES.light
}

// The ground: the record's per-scheme colour when it has one, else the
// scheme's own neutral. `background` is the DARK ground by contract.
export const whitelabelGround = (s) => {
  const b = brandOf(s)
  const scheme = schemeOf(s)
  if (scheme === SCHEMES.dark) return (b && b.background) || SCHEMES.dark.ground
  return (b && b.backgroundLight) || SCHEMES.light.ground
}

// Exported alongside schemeOf — same consumer, same drift argument.
export const accentOf = (s) => {
  const b = brandOf(s)
  return (b && b.accent) || GOLD
}

// The brand light behind the card: one soft radial of the tenant's own accent
// from the top edge, fading into the ground. Painted as a background IMAGE
// over the ground colour, so a record with no parseable accent still lands
// on its plain ground. Exported for the sibling auth pages (signup,
// reset-password, accept-invite), which paint the same backdrop on a branded
// host so the whole flow reads as one surface.
export const whitelabelBackdrop = (s) => {
  const glow = schemeOf(s).glow
  return `radial-gradient(120% 70% at 50% -10%, color-mix(in srgb, ${accentOf(s)} ${glow}, transparent), transparent 70%)`
}

// ── CONTRAST, DERIVED FROM THE TENANT'S OWN HUE (A2-01 / A2-02) ────────────
// This card paints from a RECORD, before any design system exists (a
// signed-out boot on a custom domain), which is why every value in SCHEMES is
// a literal. That is fine for the grounds — they are ours. It is NOT fine for
// anything painted against `accent`, because the accent is the TENANT'S, and a
// single fixed ink cannot be legible on every hue a customer may choose.
//
// Measured on vault.pr.symbols.app, 2026-09-09:
//   "Log in"       ink #1F2A44 on the tenant accent #5065FF = 3.15:1 (needs 4.5)
//   "VAULT PARTNERS" accent #5065FF on the light ground     = 4.11:1 (needs 4.5)
// The same two literals are correct on the DEFAULT gold accent, which is what
// they were chosen against and why this shipped.
//
// So both are derived now. `_wlLum` / `_wlContrast` are the standard WCAG
// relative-luminance pair; each helper below is self-contained (this file's
// FA204 posture) and every one of them falls back to today's literal when a
// colour cannot be parsed, so an unparseable record can never blank a control.
const _wlRgb = (c) => {
  const v = String(c || '').trim()
  const hex = v.charAt(0) === '#' ? v.slice(1) : null
  if (hex && (hex.length === 3 || hex.length === 6)) {
    const f = hex.length === 3 ? hex.replace(/./g, (ch) => ch + ch) : hex
    const n = parseInt(f, 16)
    if (!Number.isNaN(n)) return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  const m = v.match(/rgba?\(\s*([0-9.]+)[,\s]+([0-9.]+)[,\s]+([0-9.]+)/i)
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])]
  return null
}

const _wlLum = (rgb) => {
  const ch = rgb.map((v) => {
    const x = v / 255
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2]
}

const _wlContrast = (a, b) => {
  const ra = _wlRgb(a)
  const rb = _wlRgb(b)
  if (!ra || !rb) return null
  const la = _wlLum(ra)
  const lb = _wlLum(rb)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

// The ink for anything painted ON the accent. Keeps the scheme's own literal
// whenever it already clears AA (so the default gold button is untouched,
// pixel for pixel), and falls to white only when it does not.
export const onAccentFor = (s) => {
  const fixed = schemeOf(s).onAccent
  const accent = accentOf(s)
  const c = _wlContrast(fixed, accent)
  if (c === null || c >= 4.5) return fixed
  // White is right on a dark accent and black on a light one; a MID hue needs
  // whichever wins, not a fixed preference — grey is the case where guessing
  // white lands at 3.9:1 and still fails.
  const white = _wlContrast('#FFFFFF', accent) || 0
  const black = _wlContrast('#111820', accent) || 0
  return white >= black ? '#FFFFFF' : '#111820'
}

// The accent as INK on a ground. A brand hue is chosen to be seen as a FILL;
// as text on a near-white ground it is routinely a few tenths short. Darken it
// in place — same hue, same brand — until it clears AA. 12 steps of 8% reach
// black-adjacent, so the loop always terminates and always ends legible.
export const accentInkOn = (s, ground) => {
  const accent = accentOf(s)
  const rgb = _wlRgb(accent)
  if (!rgb || _wlRgb(ground) === null) return accent
  if (_wlContrast(accent, ground) >= 4.5) return accent
  let cur = rgb
  for (let i = 0; i < 12; i++) {
    cur = cur.map((v) => Math.round(v * 0.92))
    const hex =
      '#' + cur.map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')
    if (_wlContrast(hex, ground) >= 4.5) return hex
  }
  return '#' + cur.map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('')
}
const hasPersonas = (s) => {
  const b = brandOf(s)
  return !!(b && Array.isArray(b.personaSignins) && b.personaSignins.length)
}

// Size the record's SVG via INLINE ATTRS, never an `& svg` selector block.
// Measured live 2026-08-19: the stored markup carries only a `viewBox`, and
// the selector block is dropped by published css-in-props builds (the trap
// LogoLoader.js documents) — the mark rendered 0x0, an invisible logo.
// Exported with schemeOf/accentOf (2026-08-30) — the request-access gate
// renders the same mark and must not re-learn the 0x0-logo trap below.
//
// ASPECT-AWARE (whitelabel-visual-astio 9cbc70ec4, ported 2026-09-29): a
// square `width=px height=px` crushed a non-square record mark — a 232×48
// wordmark painted 44×9 px inside a 44×44 box, an unreadable sliver. The
// record's own viewBox is the truth: height is the knob, the width follows the
// ratio, capped at 5.4 so a banner-shaped mark cannot blow the lockup open.
// No viewBox, or an unreadable one: the square box, as before.
export const sizedSvg = (markup, px) => {
  if (typeof markup !== 'string' || !markup) return ''
  if (!/^\s*<svg\b/i.test(markup)) return markup
  const vb = markup.match(
    /viewBox\s*=\s*["']\s*([\d.+-]+)[ ,]+([\d.+-]+)[ ,]+([\d.+-]+)[ ,]+([\d.+-]+)/i
  )
  let w = px
  if (vb) {
    const ratio = parseFloat(vb[3]) / Math.max(1e-6, parseFloat(vb[4]))
    if (isFinite(ratio) && ratio > 0) w = Math.round(Math.min(ratio, 5.4) * px)
  }
  return markup
    .replace(/\s(width|height)\s*=\s*(".*?"|'.*?')/gi, '')
    .replace(/^\s*<svg\b/i, `<svg width="${w}" height="${px}" preserveAspectRatio="xMidYMid meet"`)
}

// Kick the org's public onboarding config exactly once, then park it on
// state. The role list is NEVER hardcoded: it is an allowlist published by
// the org itself, so "every type except admin" is the SERVER's decision (the
// four published roles carry no admin) and a copy change needs no redeploy.
// Guarded through state rather than a module flag so a remount re-reads.
const kickOnboarding = (el, s) => {
  const b = brandOf(s)
  if (!b || !b.workspaceKey || s.onboardingKicked) return
  s.update({ onboardingKicked: true }, { preventUpdate: true })
  Promise.resolve(el.call('fetchWhitelabelOnboarding', { workspaceKey: b.workspaceKey }))
    .then((cfg) => {
      // null = unconfigured, disabled, or unreachable — all indistinguishable
      // to a visitor, so the whole signup affordance simply stays hidden.
      if (cfg && cfg.roles && cfg.roles.length) {
        s.update({ onboardingRoles: cfg.roles, onboardingCaptcha: cfg.captcha || null })
      }
    })
    .catch(() => {})
}

// The signup affordance appears only once the org has published a usable
// role list — an org with no public onboarding shows a pure login screen.
const canSignUp = (s) => !!(s && s.onboardingRoles && s.onboardingRoles.length)

// Which mode the card is in. Signup can never be the mode while the org has
// published nothing, so a late-arriving null config cannot strand a visitor
// on a form they are unable to submit.
const isSignup = (s) => s && s.mode === 'signup' && canSignUp(s)

const PERSONA_TINTS = [GOLD, '#1D9E75', '#2F5FA5', '#6B4EA0']
const personaTint = (key) => {
  const k = typeof key === 'string' ? key : ''
  let h = 0
  for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0
  return PERSONA_TINTS[h % PERSONA_TINTS.length]
}

// The org LOCKUP — logo + name (+ tagline, accent rule) — shared by the branded
// sign-in and the invitation cards (accept-invite, signup: Nika 2026-10-11
// "Yes, org logo on the cards"). The branded sign-in places this exact object;
// the cards add their own "no brand → nothing" hide. No logo → an empty glyph.
//
// 2026-10-14 (Nika: "make it more beautiful"): the name is the record's own
// spelling in the scheme's INK, not an upper-cased, wide-tracked accent run —
// that read at low contrast and upper-cased whatever script the record holds
// (Georgian is never upper-cased). The accent stays on the mark, where a brand
// hue reads as a fill. The tagline is ink-soft at a readable size; the old
// accent rule under it is gone (the card below is the separator).
export const WhitelabelLockup = {
  display: 'flex',
  flow: 'y',
  align: 'center center',
  gap: 'Y',
  margin: '0 0 B',
  // ⚠️ Named `Logo`, NOT `Mark`, and with an explicit tag: a child keyed
  // `Mark` renders an HTML <mark> element, which every browser paints with
  // a default YELLOW background — measured `rgb(255,255,0)` behind the
  // logo on app.bellforge.eu (Nika: "remove yellow bg on bellforge logo").
  // That colour was never in the design system; it was UA styling arriving
  // through the key name.
  BrandGlyph: {
    tag: 'div',
    display: 'flex',
    align: 'center center',
    margin: '0 0 Y',
    color: (el, s) => accentOf(s),
    html: (el, s) => {
      const b = brandOf(s)
      return sizedSvg(b && b.logoSvg, 48)
    },
    aria: { hidden: 'true' }
  },
  Wordmark: {
    tag: 'h1',
    fontSize: 'C',
    fontWeight: '700',
    letterSpacing: '-0.01em',
    lineHeight: '1.1',
    textAlign: 'center',
    margin: '0',
    color: (el, s) => schemeOf(s).ink,
    text: (el, s) => {
      const b = brandOf(s)
      return (b && b.name) || el.call('polyglot', 'auth.whitelabel.wordmarkFallback')
    }
  },
  Tagline: {
    tag: 'div',
    fontSize: 'Z',
    fontWeight: '500',
    letterSpacing: '0.08em',
    lineHeight: '1.4',
    color: (el, s) => schemeOf(s).inkSoft,
    textAlign: 'center',
    display: (el, s) => {
      const b = brandOf(s)
      return b && b.tagline ? 'block' : 'none'
    },
    text: (el, s) => {
      const b = brandOf(s)
      return (b && b.tagline) || ''
    }
  }
}

export const WhitelabelSignin = {
  tag: 'section',
  position: 'fixed',
  inset: '0',
  width: 'auto',
  minHeight: '0',
  overflowY: 'auto',
  display: 'flex',
  flow: 'y',
  align: 'center center',
  padding: 'C A',
  // ⚠️ THE WHOLE SURFACE is the fixed full-viewport layer — there is no
  // separate ground element.
  // Measured on app.bellforge.eu: the shell's routed `content` wrapper
  // carries a responsive `padding-left/right` (44.5px at a 2560px viewport),
  // so a background on an in-flow element starts at x=45 and leaves dark
  // bands — Nika: "black edges". Making THIS element `position: fixed;
  // inset: 0` is immune to every ancestor's padding, needs no `100vw` (which
  // adds scrollbar-width overflow) and no negative margins tuned to a token.
  //
  // ⚠️ DO NOT go back to a separate fixed `Ground` CHILD. That is what the
  // previous build did and it PAINTED OVER the whole screen: a positioned
  // child paints above its static in-flow siblings, and this runtime is
  // known to drop `zIndex` (project_module_runtime_drops_transform_zindex),
  // so the intended z-order never applied. It reported as "login screen
  // looks empty" / "bare navy with only the accent rule". It also defeated
  // the obvious check — the ground carried `pointerEvents: 'none'`, so
  // `elementFromPoint` returned the text UNDERNEATH and the overlay looked
  // innocent while still covering it.
  color: (el, s) => schemeOf(s).ink,
  background: (el, s) => whitelabelGround(s),
  backgroundImage: (el, s) => whitelabelBackdrop(s),
  backgroundRepeat: 'no-repeat',

  state: {
    loading: false,
    // Which auth entry is in flight: '' | 'form' | 'google' | a persona key.
    // ONE slot on purpose — the card has four ways in (form, Google, N demo
    // personas) and they must be mutually exclusive: two concurrent sign-ins
    // race each other's tokens. Every entry checks it before starting, and a
    // successful persona/Google sign-in deliberately never clears it — the
    // busy label holds until the full-page reload lands, so there is no
    // "clicked, nothing happening" gap (Nika: "add loading state on
    // signin/demo signins").
    authBusy: '',
    identifier: '',
    password: '',
    error: '',
    // Signup ("request an invitation") — a second MODE of this one card
    // rather than a second surface, so the brand lockup, scheme and ground
    // are shared and cannot drift apart.
    mode: 'signin',
    onboardingKicked: false,
    onboardingRoles: null,
    onboardingCaptcha: null,
    // Written by WhitelabelTurnstile, read by SuSubmit. Empty until the
    // widget issues a token, and empty again after every attempt — a
    // Turnstile token is single-use.
    captchaToken: '',
    captchaWidgetId: null,
    suName: '',
    suEmail: '',
    suRole: '',
    suBusy: false,
    suStatus: '',
    suOk: false
  },

  // The brand lives on ROOT state and can arrive AFTER first paint (the
  // late-apply path in whitelabelBrand.js). Without declaring the key this
  // element never re-renders on that write. Declared lists are the binding
  // contract in this build.
  subscribeTo: ['whitelabelBrand'],

  // The tab title is the sign-in PAGE's metadata (pages/auth/signin.js: the
  // org name on a branded host), which helmet re-runs when the brand lands —
  // this surface writes no document.title.
  onRender: (el, s) => {
    kickOnboarding(el, s)
  },
  // The brand (and with it the workspace key) can land after first paint.
  onUpdate: (el, s) => {
    kickOnboarding(el, s)
  },

  Lockup: WhitelabelLockup,

  // ONE card, one measure (Nika 2026-10-14): with no demo personas the board
  // is the login card alone, at the overlay-small measure every auth card on
  // a branded host shares — it used to stretch to the two-card width (~870px
  // of form). Personas on: the two-card measure.
  Board: {
    display: 'flex',
    align: 'stretch center',
    gap: 'A',
    width: '100%',
    maxWidth: (el, s) => (hasPersonas(s) ? 'overlayXl' : 'overlaySm'),
    '@media (max-width: 780px)': { flow: 'column', maxWidth: 'overlaySm' },

    // LEFT — demo personas. Absent unless the org enabled them: the server
    // omits `personaSignins` unless the flag is on AND the channel is
    // non-production.
    Demo: {
      flex: '1 1 0',
      minWidth: '0',
      padding: 'B1',
      '@mobileL': { padding: 'B' },
      round: 'radiusCard',
      background: (el, s) => schemeOf(s).card,
      boxShadow: (el, s) => schemeOf(s).cardShadow,
      display: (el, s) => (hasPersonas(s) ? 'block' : 'none'),

      CardHead: {
        display: 'flex',
        align: 'center left',
        gap: 'Y',
        margin: '0 0 X',
        AccentBar: {
          width: '3px',
          height: '15px',
          round: 'radiusPill',
          flex: 'none',
          background: (el, s) => accentOf(s)
        },
        HeadText: {
          tag: 'div',
          fontSize: 'Z1',
          fontWeight: '700',
          color: (el, s) => schemeOf(s).ink,
          text: '{{ auth.whitelabel.demo.title | polyglot }}'
        }
      },
      Sub: {
        tag: 'div',
        fontSize: 'Z',
        color: (el, s) => schemeOf(s).inkSoft,
        margin: '0 0 A',
        text: '{{ auth.whitelabel.demo.hint | polyglot }}'
      },
      Rows: {
        display: 'flex',
        flow: 'y',
        gap: 'V',
        childExtends: 'WhitelabelPersonaRow',
        childrenAs: 'state',
        children: (el, s) => {
          const b = brandOf(s)
          return ((b && b.personaSignins) || [])
            .filter((r) => r && r.key && r.label)
            .map((r) => ({ key: r.key, label: r.label }))
        }
      }
    },

    // RIGHT — real sign-in: email/password + Google. Symmetric inset on all
    // four sides; the heading block is centred under the centred lockup.
    Login: {
      flex: '1 1 0',
      minWidth: '0',
      padding: 'B1',
      '@mobileL': { padding: 'B' },
      round: 'radiusCard',
      background: (el, s) => schemeOf(s).card,
      boxShadow: (el, s) => schemeOf(s).cardShadow,

      CardHead: {
        display: 'flex',
        align: 'center center',
        margin: '0 0 X',
        HeadText: {
          tag: 'div',
          fontSize: 'B',
          fontWeight: '700',
          lineHeight: '1.2',
          textAlign: 'center',
          color: (el, s) => schemeOf(s).ink,
          // The org's own name, never a literal — this surface serves every
          // white-label host, not just one.
          text: (el, s) => {
            if (!isSignup(s)) return el.call('polyglot', 'auth.whitelabel.head.login')
            const b = brandOf(s)
            // The branded form interpolates the org name into ONE key rather
            // than concatenating two fragments — a translation orders the
            // words its own way.
            return b && b.name
              ? el.call('polyglot', 'auth.whitelabel.head.signupBranded').replace('{brand}', b.name)
              : el.call('polyglot', 'auth.whitelabel.head.signup')
          }
        }
      },
      Sub: {
        tag: 'div',
        fontSize: 'Z1',
        lineHeight: '1.5',
        textAlign: 'center',
        color: (el, s) => schemeOf(s).inkSoft,
        margin: '0 0 A',
        text: (el, s) =>
          el.call(
            'polyglot',
            isSignup(s) ? 'auth.whitelabel.sub.signup' : 'auth.whitelabel.sub.login'
          )
      },

      Form: {
        tag: 'form',
        display: (el, s) => (isSignup(s) ? 'none' : 'flex'),
        flow: 'y',
        gap: 'Y',
        onSubmit: async (e, el, s) => {
          e.preventDefault()
          if (s.loading || s.authBusy) return
          const identifier = String(s.identifier || '').trim()
          const password = String(s.password || '')
          if (!identifier || !password) {
            s.update({ error: el.call('polyglot', 'auth.whitelabel.needCredentials') })
            return
          }
          s.update({ loading: true, authBusy: 'form', error: '' })
          try {
            const loginResult = await el.sdk.login(identifier, password)
            // A sign-in on this origin: its refresh token is its own (review D1).
            el.call('markOwnSession')
            await el.call('initializeUserSession', { loginData: loginResult })
            if (typeof window !== 'undefined') window.location.assign('/')
          } catch (err) {
            s.update({
              loading: false,
              authBusy: '',
              error: (err && err.message) || el.call('polyglot', 'auth.whitelabel.signinFailed')
            })
          }
        },

        EmailCaption: wlCaption('auth.whitelabel.email'),
        Email: wlField({
          type: 'text',
          autocomplete: 'username',
          placeholder: 'you@example.com',
          onInput: (e, el, s) => s.update({ identifier: e.target.value }, { preventUpdate: true })
        }),

        PasswordCaption: wlCaption('auth.whitelabel.password'),
        Password: wlField({
          type: 'password',
          autocomplete: 'current-password',
          placeholder: '••••••••',
          onInput: (e, el, s) => s.update({ password: e.target.value }, { preventUpdate: true })
        }),

        Err: {
          tag: 'div',
          fontSize: 'Y',
          color: (el, s) => schemeOf(s).danger,
          display: (el, s) => (s.error ? 'block' : 'none'),
          text: (el, s) => s.error || ''
        },

        SubmitBtn: wlPrimary({
          type: 'submit',
          margin: 'Z 0 0',
          opacity: (el, s) => (s.loading ? '0.6' : '1'),
          text: (el, s) =>
            el.call('polyglot', s.loading ? 'auth.whitelabel.signingIn' : 'auth.whitelabel.submit')
        })
      },

      OrRow: {
        // Hidden in signup: that flow mints an INVITATION, so an OAuth button
        // beside it would promise an account it cannot create.
        display: (el, s) => (isSignup(s) ? 'none' : 'flex'),
        align: 'center center',
        gap: 'Z',
        margin: 'A 0',
        L1: { flex: '1', height: 'hairline', background: (el, s) => schemeOf(s).rule },
        W: {
          tag: 'span',
          fontSize: 'Z',
          color: (el, s) => schemeOf(s).inkSoft,
          text: '{{ auth.whitelabel.or | polyglot }}'
        },
        L2: { flex: '1', height: 'hairline', background: (el, s) => schemeOf(s).rule }
      },

      // Google — the SAME SDK path the shared login uses (initCodeClient
      // popup → sdk.googleAuthCallback), not a reimplementation of the auth.
      // Hidden when the environment carries no client id, so it can never
      // render a dead button.
      // An outlined control with the provider's own glyph from the design
      // system's icon set (`googleLogoNormal`, the same glyph the platform
      // login shows) — never inline SVG (Rule 62).
      GoogleBtn: wlSecondary({
        Icon: { icon: 'googleLogoNormal', boxSize: 'iconMd', flex: 'none' },
        GoogleLabel: {
          tag: 'span',
          text: (el, s) =>
            el.call(
              'polyglot',
              s.authBusy === 'google' ? 'auth.whitelabel.signingIn' : 'auth.whitelabel.google'
            )
        },
        opacity: (el, s) => (s.authBusy && s.authBusy !== 'google' ? '0.5' : '1'),
        display: (el, s) => {
          if (isSignup(s)) return 'none'
          const env = el.sdk && el.sdk.environment
          return env && env.googleClientId ? 'flex' : 'none'
        },
        onClick: async (e, el, s) => {
          if (s.authBusy) return
          const env = el.sdk && el.sdk.environment
          const clientId = env && env.googleClientId
          const g = typeof window !== 'undefined' ? window.google : null
          if (!clientId || !g || !g.accounts || !g.accounts.oauth2) {
            s.update({ error: el.call('polyglot', 'auth.whitelabel.googleUnavailable') })
            return
          }
          try {
            const client = g.accounts.oauth2.initCodeClient({
              client_id: clientId,
              scope: 'openid email profile',
              ux_mode: 'popup',
              callback: async (resp) => {
                if (!resp || !resp.code) return
                // Busy from the exchange onward only: a popup the visitor
                // dismisses never reaches this callback, so claiming the slot
                // any earlier would strand the whole card busy forever.
                s.update({ authBusy: 'google' })
                try {
                  const loginResult = await el.sdk.googleAuthCallback(resp.code, 'postmessage')
                  // A sign-in on this origin: its refresh token is its own (review D1).
                  el.call('markOwnSession')
                  await el.call('initializeUserSession', { loginData: loginResult })
                  if (typeof window !== 'undefined') window.location.assign('/')
                } catch (err) {
                  s.update({
                    authBusy: '',
                    error:
                      (err && err.message) || el.call('polyglot', 'auth.whitelabel.googleFailed')
                  })
                }
              }
            })
            client.requestCode()
          } catch (err) {
            s.update({ error: el.call('polyglot', 'auth.whitelabel.googleUnavailable') })
          }
        }
      }),

      // ── SIGNUP: request an invitation ──────────────────────────────────
      // Deliberately NOT a password form. The programme's own landing states
      // it outright ("An invitation, not a password form"): this mints a
      // workspace invitation, and accepting that invitation is what creates
      // the account. Building credentials here would be a second
      // account-creation path for the same org.
      SignupPane: {
        display: (el, s) => (isSignup(s) ? 'flex' : 'none'),
        flow: 'y',
        gap: 'Y',

        RoleCaption: wlCaption('auth.whitelabel.request.role'),
        RoleList: {
          display: 'flex',
          flow: 'y',
          gap: 'V',
          margin: '0 0 X',
          childExtends: 'WhitelabelRoleRow',
          childrenAs: 'state',
          children: (el, s) => s.onboardingRoles || []
        },

        NameCaption: wlCaption('auth.whitelabel.request.name'),
        SuName: wlField({
          type: 'text',
          autocomplete: 'name',
          placeholder: (el) => el.call('polyglot', 'auth.whitelabel.request.namePlaceholder'),
          onInput: (e, el, s) => s.update({ suName: e.target.value }, { preventUpdate: true })
        }),

        EmailCaption: wlCaption('auth.whitelabel.request.email'),
        SuEmail: wlField({
          type: 'email',
          autocomplete: 'email',
          placeholder: 'jane@firm.com',
          onInput: (e, el, s) => s.update({ suEmail: e.target.value }, { preventUpdate: true })
        }),

        // One reserved line for both outcomes, so a result never shifts the
        // card (zero-shift: the space is held whether or not it is filled).
        SuStatus: {
          tag: 'div',
          fontSize: 'Y',
          minHeight: 'X',
          color: (el, s) => (s.suOk ? schemeOf(s).ink : schemeOf(s).danger),
          text: (el, s) => s.suStatus || ''
        },

        // Below the status line on purpose: the server's `captcha_failed`
        // message says "complete the verification below", so the widget has
        // to BE below it. Renders nothing when the org configured no
        // captcha — `onboardingCaptcha` is null and the slot stays out.
        WhitelabelTurnstile: {},

        SuSubmit: wlPrimary({
          type: 'button',
          margin: 'Y 0 0',
          opacity: (el, s) => (s.suBusy ? '0.6' : '1'),
          text: (el, s) =>
            el.call(
              'polyglot',
              s.suBusy ? 'auth.whitelabel.request.sending' : 'auth.whitelabel.request.submit'
            ),
          onClick: async (e, el, s) => {
            if (s.suBusy) return
            const b = brandOf(s)
            s.update({ suBusy: true, suStatus: '', suOk: false })
            const res = await el.call('submitWhitelabelOnboarding', {
              workspaceKey: b && b.workspaceKey,
              name: s.suName,
              email: s.suEmail,
              role: s.suRole,
              // Absent when the org configured no captcha — the server then
              // never asks for one. Present and stale is the failure the
              // reset below prevents.
              captchaToken: s.captchaToken
            })
            // Every attempt spends the token, pass or fail. Reset before the
            // visitor can press again, or the second press replays a dead
            // token and reads as `captcha_failed`.
            el.call('resetWhitelabelTurnstile', { state: s })
            s.update({
              suBusy: false,
              suOk: !!(res && res.ok),
              suStatus: (res && res.message) || ''
            })
          }
        }),

        FinePrint: {
          tag: 'div',
          margin: 'V 0 0',
          fontSize: 'Y',
          color: (el, s) => schemeOf(s).inkSoft,
          text: '{{ auth.whitelabel.request.note | polyglot }}'
        }
      },

      // Only rendered once the org has actually published a role list, so a
      // login-only org never shows a door that leads nowhere.
      ModeSwitch: {
        tag: 'button',
        type: 'button',
        display: (el, s) => (canSignUp(s) ? 'block' : 'none'),
        width: '100%',
        margin: 'A 0 0',
        padding: 'Y 0',
        background: 'transparent',
        borderWidth: '0',
        cursor: 'pointer',
        fontSize: 'Z',
        fontWeight: '500',
        // The brand hue as INK, darkened only as far as AA needs on this
        // ground (A2-02) — a link-weight control, so it reads as the accent.
        color: (el, s) => accentInkOn(s, whitelabelGround(s)),
        textDecoration: 'none',
        ':hover': { textDecoration: 'underline' },
        ':active': { textDecoration: 'underline', filter: 'brightness(0.85)' },
        ':focus-visible': {
          outlineStyle: 'solid',
          outlineWidth: 'hairline2',
          outlineOffset: 'hairline2',
          outlineColor: 'currentColor'
        },
        text: (el, s) =>
          el.call(
            'polyglot',
            isSignup(s) ? 'auth.whitelabel.switchToLogin' : 'auth.whitelabel.switchToSignup'
          ),
        onClick: (e, el, s) =>
          s.update({
            mode: isSignup(s) ? 'signin' : 'signup',
            error: '',
            suStatus: '',
            suOk: false
          })
      }
    }
  },

  Foot: {
    tag: 'div',
    margin: 'A 0 0',
    fontSize: 'Y',
    color: (el, s) => schemeOf(s).inkSoft,
    textAlign: 'center',
    display: (el, s) => (hasPersonas(s) ? 'block' : 'none'),
    text: '{{ auth.whitelabel.demo.note | polyglot }}'
  }
}

// One persona row — tinted initial tile + label, whole row clickable.
export const WhitelabelPersonaRow = {
  tag: 'button',
  type: 'button',
  width: '100%',
  display: 'flex',
  align: 'center left',
  gap: 'Z',
  padding: 'Y Z',
  round: 'radiusControl',
  cursor: (el, s) => (s.parent && s.parent.authBusy ? 'default' : 'pointer'),
  borderWidth: '0',
  background: 'transparent',
  textAlign: 'left',
  ':hover': { background: 'rgba(127,127,127,0.16)' },

  // The whole row dims while ANOTHER entry is signing in; itself it swaps to
  // an explicit "Signing in…" (below) so the click always answers visibly.
  opacity: (el, s) => (s.parent && s.parent.authBusy && s.parent.authBusy !== s.key ? '0.5' : '1'),
  aria: {
    label: (el, s) =>
      el.call('polyglot', 'auth.whitelabel.persona.aria').replace('{name}', s.label || ''),
    busy: (el, s) => (s.parent && s.parent.authBusy === s.key ? 'true' : 'false')
  },

  PersonaTile: {
    display: 'flex',
    align: 'center center',
    flex: 'none',
    boxSize: 'B',
    round: 'radiusControl',
    fontSize: 'Z',
    fontWeight: '700',
    color: 'white',
    background: (el, s) => personaTint(s.key),
    // While THIS persona signs in, the initial gives way to the same flip the
    // branded loader mark uses — one motion vocabulary for "bellforge is
    // working", and no new spinner primitive.
    animation: (el, s) => (s.parent && s.parent.authBusy === s.key ? 'flipY' : 'none'),
    animationDuration: '1.2s',
    animationIterationCount: 'infinite',
    text: (el, s) =>
      String(s.label || s.key || '?')
        .trim()
        .charAt(0)
        .toUpperCase()
  },
  PersonaName: {
    tag: 'span',
    flex: '1',
    minWidth: '0',
    fontSize: 'Z1',
    fontWeight: '500',
    color: (el, s) => schemeOf(s).ink,
    text: (el, s) =>
      s.parent && s.parent.authBusy === s.key
        ? el.call('polyglot', 'auth.whitelabel.signingIn')
        : el.call('polyglot', s.label || '')
  },

  onClick: async (e, el, s) => {
    // The PARENT slot, not an element flag: `el.__busy` only stopped a
    // double-click on the SAME row — a second persona (or the form, or
    // Google) could still start a concurrent sign-in and race tokens. One
    // shared slot makes the four entry points mutually exclusive AND gives
    // the row its visible busy state, which an element property never could.
    if (s.parent && (s.parent.authBusy || s.parent.loading)) return
    if (s.parent && s.parent.update) s.parent.update({ authBusy: s.key, error: '' })
    const res = await el.call('whitelabelPersonaSignin', s.key)
    if (res && res.ok === false) {
      // Failure releases the slot; success holds it — the label stays
      // "Signing in…" until the full-page reload lands, so there is no
      // clicked-but-nothing-happening gap.
      if (s.parent && s.parent.update) {
        s.parent.update({ authBusy: '', error: res.message || '' })
      }
    }
  }
}

// One selectable account type. Extends nothing: same self-contained rule as
// the rest of this surface. Selection lives on the PARENT's `suRole`, so the
// rows stay stateless and exactly one can ever read as chosen.
export const WhitelabelRoleRow = {
  tag: 'button',
  type: 'button',
  width: '100%',
  display: 'flex',
  flow: 'y',
  gap: 'V',
  padding: 'Y Z',
  round: 'radiusControl',
  cursor: 'pointer',
  borderWidth: '0',
  textAlign: 'left',
  background: (el, s) =>
    s.parent && s.parent.suRole === s.key ? 'rgba(127,127,127,0.16)' : 'transparent',
  boxShadow: (el, s) => (s.parent && s.parent.suRole === s.key ? schemeOf(s).hairline : 'none'),
  ':hover': { background: 'rgba(127,127,127,0.16)' },
  // The pressed state is what a screen reader has to read here — a colour
  // change alone would announce nothing.
  aria: {
    pressed: (el, s) => (s.parent && s.parent.suRole === s.key ? 'true' : 'false'),
    label: (el, s) =>
      el.call('polyglot', 'auth.whitelabel.role.aria').replace('{name}', s.label || '')
  },

  RoleName: {
    tag: 'span',
    fontSize: 'Z1',
    fontWeight: '500',
    color: (el, s) => schemeOf(s).ink,
    text: (el, s) => s.label
  },
  RoleDesc: {
    tag: 'span',
    fontSize: 'Y',
    color: (el, s) => schemeOf(s).inkSoft,
    display: (el, s) => (s.description ? 'block' : 'none'),
    text: (el, s) => s.description || ''
  },

  onClick: (e, el, s) => {
    if (s.parent && s.parent.update) s.parent.update({ suRole: s.key, suStatus: '', suOk: false })
  }
}
